// ============================================================
// VidyaTrack — Parent Dashboard
// ============================================================

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FiBook, FiCheckSquare, FiBell, FiTrendingUp } from "react-icons/fi";
import { useAuth } from "../../contexts/AuthContext";
import { useData } from "../../contexts/DataContext";
import { format, isPast } from "date-fns";
import StatCard from "../../components/shared/StatCard";

// Safe date converter (handles Firestore Timestamps)
const toDate = v => { if(!v) return new Date(); if(typeof v?.toDate==="function") return v.toDate(); return new Date(v); };



export default function ParentDashboard() {
  const { userData } = useAuth();


  const { getHomework, getSubmissions, getAttendance, getNotices, getEvents } = useData();
  const navigate = useNavigate();

  const [hw,      setHw]      = useState([]);
  const [subs,    setSubs]    = useState([]);
  const [att,     setAtt]     = useState([]);
  const [notices, setNotices] = useState([]);
  const [events,  setEvents]  = useState([]);
  const [loading, setLoading] = useState(true);

  const childId = userData?.children?.[0];

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const load = async () => {
      try {
        const [h, s, a, n, e] = await Promise.all([
          getHomework({}),
          getSubmissions({ studentId: childId }),
          getAttendance({ studentId: childId }),
          getNotices({ audience: "parents" }),
          getEvents(),
        ]);
        setHw(h); setSubs(s); setAtt(a);
        setNotices(n.slice(0, 5));
        setEvents(e.filter(ev => !isPast(toDate(ev.date))).slice(0, 4));
      } catch { /* handled silently */ }
      finally { setLoading(false); }
    };
    if (childId) load();
    else setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [childId]);

  const subMap    = new Map(subs.map(s => [s.homeworkId, s]));
  const pendingHw = hw.filter(h => !subMap.has(h.id) && !isPast(toDate(h.deadline)));
  const overdueHw = hw.filter(h => !subMap.has(h.id) && isPast(toDate(h.deadline)));
  const present   = att.filter(a => a.status === "present").length;
  const attPct    = att.length > 0 ? Math.round((present / att.length) * 100) : 0;
  const hwPct     = hw.length  > 0 ? Math.round((subs.length / hw.length) * 100) : 0;

  if (loading) return <div className="loading-center"><div className="spinner"/></div>;

  if (!childId) return (
    <div className="fade-in">
      <div className="page-head"><div><h1 className="page-title">Parent Dashboard 👨‍👩‍👧</h1></div></div>
      <div className="card">
        <div className="empty-state">
          <div className="empty-icon">👦</div>
          <div className="empty-title">No student linked</div>
          <div className="empty-desc">Your account has no student linked yet. Please contact the school administrator.</div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="fade-in">
      <div className="page-head">
        <div>
          <h1 className="page-title">Parent Dashboard 👨‍👩‍👧</h1>
          <p className="page-sub">
            {format(new Date(), "EEEE, MMMM d, yyyy")}
            {userData?.childName ? ` · Monitoring: ${userData.childName}` : ""}
          </p>
        </div>
      </div>

      {/* Child profile strip */}
      <div style={{ background:"linear-gradient(135deg,#054bb4,#2e5caf)", borderRadius:"var(--r12)", padding:"20px 24px", marginBottom:20, display:"flex", alignItems:"center", gap:18, flexWrap:"wrap" }}>
        <div style={{ width:56, height:56, borderRadius:"50%", background:"rgba(255,255,255,.18)", display:"flex", alignItems:"center", justifyContent:"center", fontWeight:800, fontSize:"1.2rem", color:"#fff", flexShrink:0 }}>
          {(userData?.childName||"?").split(" ").map(n=>n[0]).join("").slice(0,2)}
        </div>
        <div style={{ flex:1 }}>
          <h3 style={{ color:"#fff", fontSize:"1.1rem", marginBottom:4 }}>{userData?.childName || "Student"}</h3>
          <p style={{ color:"rgba(255,255,255,.7)", fontSize:".84rem", margin:0 }}>
            {userData?.childClass || ""} · {userData?.school || ""}
          </p>
        </div>
        <div style={{ display:"flex", gap:28, flexWrap:"wrap" }}>
          {[
            { label:"Attendance", value:`${attPct}%` },
            { label:"HW Done",    value:`${hwPct}%` },
            { label:"Pending",    value:pendingHw.length },
          ].map(s => (
            <div key={s.label} style={{ textAlign:"center" }}>
              <div style={{ fontSize:"1.5rem", fontWeight:800, color:"#fff", fontFamily:"'Poppins',sans-serif", lineHeight:1 }}>{s.value}</div>
              <div style={{ fontSize:".72rem", color:"rgba(255,255,255,.65)", marginTop:3 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="g g4" style={{ marginBottom:20 }}>
        <StatCard icon={<FiBook/>}       label="Pending HW"     value={pendingHw.length} color="var(--blue-700)" bg="var(--blue-100)"
          onClick={() => navigate("/parent/homework")}/>
        <StatCard icon={<FiCheckSquare/>} label="Attendance"    value={`${attPct}%`}      color="var(--green-600)"    bg="var(--green-50)"
          onClick={() => navigate("/parent/attendance")}/>
        <StatCard icon={<FiTrendingUp/>} label="HW Completion"  value={`${hwPct}%`}       color="#7c3aed"         bg="#f5f3ff"/>
        <StatCard icon={<FiBell/>}       label="New Notices"    value={notices.filter(n=>n.priority==="high").length} color="var(--red-600)" bg="var(--red-50)"
          onClick={() => navigate("/parent/notices")}/>
      </div>

      {overdueHw.length > 0 && (
        <div style={{ background:"var(--red-50)", border:"1px solid var(--red-100)", borderRadius:"var(--r8)", padding:"12px 16px", marginBottom:16, fontSize:".86rem", color:"var(--red-600)", fontWeight:600, display:"flex", gap:10 }}>
          <span>⚠️</span>
          <div>
            <div>Your child has {overdueHw.length} overdue assignment{overdueHw.length>1?"s":""}.</div>
            <div style={{ fontWeight:400, marginTop:2 }}>Please remind them to speak with their teacher.</div>
          </div>
        </div>
      )}

      <div style={{ display:"grid", gridTemplateColumns:"1fr 300px", gap:16, alignItems:"start" }}>
        <div style={{ display:"flex", flexDirection:"column", gap:14 }}>

          {/* Homework status table */}
          <div className="card">
            <div className="card-header">
              <span className="card-title">📚 Homework Status</span>
              <button className="btn btn-secondary btn-sm" onClick={() => navigate("/parent/homework")}>View All</button>
            </div>
            <div className="card-body" style={{ padding:0 }}>
              {hw.length === 0 ? (
                <div className="empty-state"><div className="empty-icon">📚</div><div className="empty-title">No homework assigned yet</div></div>
              ) : (
                <div className="tbl-wrap">
                  <table className="data-tbl">
                    <thead><tr><th>Assignment</th><th>Subject</th><th>Due</th><th>Status</th><th>Grade</th></tr></thead>
                    <tbody>
                      {hw.slice(0, 8).map(h => {
                        const sub = subMap.get(h.id);
                        const over = isPast(toDate(h.deadline));
                        return (
                          <tr key={h.id}>
                            <td style={{ fontWeight:600, fontSize:".88rem" }}>{h.title}</td>
                            <td><span className="badge badge-blue" style={{ fontSize:".65rem" }}>{h.subject}</span></td>
                            <td style={{ fontSize:".82rem", color:over&&!sub?"var(--red-600)":"var(--gray-700)" }}>{format(toDate(h.deadline),"MMM d")}</td>
                            <td>
                              <span className={`badge ${sub ? (sub.status==="graded"?"badge-green":"badge-info") : over ? "badge-red" : "badge-amber"}`} style={{ fontSize:".68rem" }}>
                                {sub ? (sub.status==="graded"?"Graded":"Submitted") : over ? "Overdue" : "Pending"}
                              </span>
                            </td>
                            <td style={{ fontWeight:700, color:"var(--green-600)", fontSize:".84rem" }}>{sub?.grade || "—"}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* Attendance summary */}
          <div className="card">
            <div className="card-header">
              <span className="card-title">✅ Attendance Record</span>
              <button className="btn btn-secondary btn-sm" onClick={() => navigate("/parent/attendance")}>Full Record</button>
            </div>
            <div className="card-body">
              {att.length === 0 ? (
                <div className="empty-state" style={{ padding:"20px 0" }}><div className="empty-icon">📅</div><div className="empty-title">No records yet</div></div>
              ) : (
                <>
                  <div className="g g4" style={{ marginBottom:14 }}>
                    {[
                      { label:"Present", v:present,                                         c:"var(--green-600)", bg:"var(--green-50)" },
                      { label:"Absent",  v:att.filter(a=>a.status==="absent").length,       c:"var(--red-600)",   bg:"var(--red-50)" },
                      { label:"Late",    v:att.filter(a=>a.status==="late").length,         c:"var(--amber-600)", bg:"var(--amber-50)" },
                      { label:"Rate",    v:`${attPct}%`,                                    c:attPct>=75?"var(--green-600)":"var(--red-600)", bg:attPct>=75?"var(--green-50)":"var(--red-50)" },
                    ].map(s => (
                      <div key={s.label} style={{ background:s.bg, borderRadius:"var(--r8)", padding:"11px", textAlign:"center" }}>
                        <div style={{ fontSize:"1.2rem", fontWeight:800, color:s.c, fontFamily:"'Poppins',sans-serif", lineHeight:1 }}>{s.v}</div>
                        <div style={{ fontSize:".7rem", color:"var(--gray-500)", marginTop:3 }}>{s.label}</div>
                      </div>
                    ))}
                  </div>
                  {attPct < 75 && (
                    <div style={{ background:"var(--red-50)", borderRadius:"var(--r6)", padding:"10px 12px", border:"1px solid var(--red-100)", fontSize:".82rem", color:"var(--red-600)", fontWeight:600 }}>
                      ⚠️ Attendance ({attPct}%) is below the required 75%. Please ensure your child attends school regularly.
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Right */}
        <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
          {events.length > 0 && (
            <div className="card">
              <div className="card-header"><span className="card-title">📅 Upcoming Events</span></div>
              <div className="card-body" style={{ padding:"10px 16px" }}>
                {events.map(e => (
                  <div key={e.id} style={{ display:"flex", gap:10, alignItems:"center", padding:"9px 0", borderBottom:"1px solid #f0f0f0" }}>
                    <div style={{ width:38, height:38, borderRadius:"var(--r8)", background:(e.color||"#054bb4")+"18", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                      <div style={{ fontSize:".78rem", fontWeight:800, color:e.color||"#054bb4", lineHeight:1 }}>{format(toDate(e.date),"d")}</div>
                      <div style={{ fontSize:".58rem", color:e.color||"#054bb4", textTransform:"uppercase" }}>{format(toDate(e.date),"MMM")}</div>
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:".82rem", fontWeight:600, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{e.title}</div>
                      <span style={{ fontSize:".65rem", fontWeight:700, padding:"1px 7px", borderRadius:20, background:(e.color||"#054bb4")+"18", color:e.color||"#054bb4" }}>{e.type}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="card">
            <div className="card-header">
              <span className="card-title">🔔 School Notices</span>
              <button className="btn btn-secondary btn-sm" onClick={() => navigate("/parent/notices")}>All</button>
            </div>
            <div className="card-body" style={{ padding:"10px 16px" }}>
              {notices.length === 0 ? (
                <div style={{ padding:"12px 0", textAlign:"center", color:"var(--gray-400)", fontSize:".84rem" }}>No notices</div>
              ) : notices.map(n => (
                <div key={n.id} style={{ padding:"10px 0", borderBottom:"1px solid #f0f0f0" }}>
                  <div style={{ fontSize:".84rem", fontWeight:600 }}>{n.title}</div>
                  <div style={{ fontSize:".72rem", color:"var(--gray-500)", marginTop:2 }}>{n.content?.slice(0,60)}…</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <style>{`@media(max-width:1024px){div[style*="grid-template-columns:1fr 300px"]{grid-template-columns:1fr!important}}`}</style>

    </div>
  );
}